import { createContext } from '@radix-ui/react-context';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useAlertCtx } from './AlertProvider';
import { useStore } from 'zustand/react';
import { useCurrentAlert, useCurrentAlertId } from './util';
import { MonitorAlertEventDto } from './types';
import { tracking } from '@/lib/tracking/tracking-emitter';
import {
  EnumAiAutomationLevel,
  EnumViewerType,
  MonitorAlertEventDto as TMonitorAlertEventDto,
} from '@/lib/api/types.gen';
import { AlertEventVisibilityType, useAlertSettingsStore } from './utils/alert-settings-store';

export type EventWithVisibility = MonitorAlertEventDto & {
  _index: number;
  _isVisible: boolean;
  _hasEscalation?: boolean;
};

/** Events with visiblity field according to camera visibility and escalation status */
const useAlertEventsWithVisibility = (mode: VisibilityMode) => {
  const { store } = useAlertCtx();
  const hiddenCameras = useStore(store, (s) => s.hiddenCameras);
  const { data: alert } = useCurrentAlert();

  const escalatedEventIds = useMemo(() => {
    const escalatedEventIds = new Set<string>();

    for (const note of alert?.recentNotes ?? []) {
      if (note.metadata?.type === 'QuillEscalation' && note.metadata?.monitoringEventId) {
        escalatedEventIds.add(note.metadata.monitoringEventId);
      }
    }

    return escalatedEventIds;
  }, [alert?.recentNotes]);

  return useMemo(() => {
    const events: EventWithVisibility[] =
      alert?.events.map((event, idx) => {
        const isVisible =
          event.videoProcessorId != null && !hiddenCameras.has(event.videoProcessorId);
        const hasEscalation = escalatedEventIds.has(event.id);

        const shouldHide =
          mode === 'omit-quill-unescalated'
            ? !hasEscalation &&
              (event as TMonitorAlertEventDto).firstViewedByType === EnumViewerType.Quill
            : false;

        return {
          ...event,
          _index: idx,
          _isVisible: isVisible && !shouldHide,
          _hasEscalation: hasEscalation,
        };
      }) ?? [];

    return events;
  }, [alert?.events, escalatedEventIds, hiddenCameras, mode]);
};

export const findNextUnviewedEventId = (
  events: EventWithVisibility[],
  currentPtr: number
): string | undefined => {
  for (let i = currentPtr + 1; i < events.length; i++) {
    const event = events[i];
    if (!event.firstViewedAt && event._isVisible) {
      return event.id;
    }
  }
  return undefined;
};

export type VisibilityMode = 'all' | 'omit-quill-unescalated';

type AlertEventsContext = {
  selectedEventId: string | undefined;
  nextEventId: string | undefined;
  prevEventId: string | undefined;
  nextUnviewedEventId: string | undefined;
  unviewedCount: number;
  totalEvents: number;
  selectedEvent: EventWithVisibility | undefined;
  events: EventWithVisibility[];
  setSelectedEventId: (eventId: string) => void;
  prev: () => void;
  next: () => void;
  nextUnviewed: () => void;
  first: () => void;
  last: () => void;
  mode: VisibilityMode;
  setMode: (mode: VisibilityMode) => void;
};

const [Provider, useContext] = createContext<AlertEventsContext>('AlertEventsContext');

/** Raw Radix context provider – use AlertEventsProvider for full logic. */
export { Provider as AlertEventsContextProvider };

export const AlertEventsProvider = (props: PropsWithChildren) => {
  const [selectedEventId, setSelectedEventId] = useState<string>();
  const eventVisibilityType = useAlertSettingsStore((s) => s.eventVisibility);
  // Mode is the alert-local visibility type vs. the underlying setting
  const [mode, setMode] = useState<VisibilityMode>('all');
  const alertId = useCurrentAlertId();
  const events = useAlertEventsWithVisibility(mode);
  const { data: alert } = useCurrentAlert();
  const isFullAuto = alert?.project?.aiAutomationLevel === EnumAiAutomationLevel.Full;

  useEffect(() => {
    if (!alertId) return;

    // If full auto and auto mode, set to escalated
    switch (eventVisibilityType) {
      case 'auto': {
        if (isFullAuto) {
          setMode('omit-quill-unescalated');
          break;
        }
        setMode('all');
        break;
      }
      default: {
        setMode(eventVisibilityType);
        break;
      }
    }
  }, [alertId, isFullAuto, setMode, eventVisibilityType]);

  // Create an "index" of visible events that can be efficiently-ish
  // traversed, with optional filtering of viewed events without escalations
  // Also calculate unviewed/total counts while iterating
  const { visibleIndex, unviewedCount, totalEvents } = useMemo(() => {
    const visibleIndex: { events: EventWithVisibility[]; ptr: number } = {
      events: [],
      ptr: 0,
    };

    let unviewedCount = 0;
    const totalEvents = events.length;

    /** Index of selected event in total events array */
    let selectedEventIdx: number | undefined = undefined;

    for (const _idx in events) {
      const event = events[_idx];
      if (event == null) continue;

      // Count unviewed events
      if (!event.firstViewedAt) {
        unviewedCount++;
      }

      const isSelectedEvent = event.id === selectedEventId;

      if (isSelectedEvent) {
        selectedEventIdx = Number(_idx);
      }

      if (event._isVisible) {
        visibleIndex.events.push(event);

        if (isSelectedEvent) {
          visibleIndex.ptr = visibleIndex.events.length - 1;
        }
      }
    }

    return { visibleIndex, unviewedCount, totalEvents };
  }, [events, selectedEventId]);
  const selectedEvent: EventWithVisibility | undefined = visibleIndex.events[visibleIndex.ptr];
  const hasSelectedEvent = selectedEventId != null;
  const firstEventId = visibleIndex.events[0]?.id;
  const lastEventId = visibleIndex.events[visibleIndex.events.length - 1]?.id;
  const nextPtr = visibleIndex.ptr < visibleIndex.events.length - 1 ? visibleIndex.ptr + 1 : 0;
  const nextEventId = visibleIndex.events[nextPtr]?.id;
  const prevPtr = visibleIndex.ptr > 0 ? visibleIndex.ptr - 1 : visibleIndex.events.length - 1;
  const prevEventId = visibleIndex.events[prevPtr]?.id;

  const nextUnviewedEventId = useMemo(() => {
    return findNextUnviewedEventId(visibleIndex.events, visibleIndex.ptr);
  }, [visibleIndex.events, visibleIndex.ptr]);

  const navigateToEvent = useCallback(
    (eventId: string) => {
      if (!eventId || eventId === selectedEventId) return;

      console.debug('Navigating to:', eventId);
      setSelectedEventId(eventId);
    },
    [selectedEventId]
  );

  const first = useCallback(() => {
    console.debug('Going to first event', firstEventId);
    if (!firstEventId) return;

    navigateToEvent(firstEventId);
  }, [firstEventId, navigateToEvent]);

  const last = useCallback(() => {
    console.debug('Going to last event', lastEventId);
    if (!lastEventId) return;

    navigateToEvent(lastEventId);
  }, [lastEventId, navigateToEvent]);

  /** Updates the selected event to the previous one, looping enabled. */
  const prev = useCallback(() => {
    console.debug('Going to previous event', prevEventId);
    if (!prevEventId) return;

    navigateToEvent(prevEventId);
  }, [prevEventId, navigateToEvent]);

  /** Updates the selected event to the next one, looping enabled. */
  const next = useCallback(() => {
    console.debug('Going to next event', nextEventId);
    if (!nextEventId) return;

    navigateToEvent(nextEventId);
  }, [nextEventId, navigateToEvent]);

  const nextUnviewed = useCallback(() => {
    console.debug('Going to next unviewed event', nextUnviewedEventId);
    if (!nextUnviewedEventId) return;

    navigateToEvent(nextUnviewedEventId);
  }, [nextUnviewedEventId, navigateToEvent]);

  useEffect(() => {
    // Init selected event id to next visible, unviewed event
    if (!hasSelectedEvent) {
      // Set to first unviewed event
      for (const event of visibleIndex.events) {
        if (!event.firstViewedAt) {
          setSelectedEventId(event.id);
          return;
        }
      }

      // Otherwise set to first visible event
      setSelectedEventId(visibleIndex.events[0]?.id);
      return;
    }

    // Find nearest event by time if selected event is not visible
    const occurred = selectedEvent?.occurred;
    if (occurred != null && !selectedEvent?._isVisible) {
      const nearestEvent = [...visibleIndex.events].sort((a, b) => {
        const diffA = a.occurred
          ? new Date(a.occurred).getTime() - new Date(occurred).getTime()
          : Infinity;
        const diffB = b.occurred
          ? new Date(b.occurred).getTime() - new Date(occurred).getTime()
          : Infinity;

        return diffA - diffB;
      })[0];

      if (nearestEvent) {
        setSelectedEventId(nearestEvent.id);
        return;
      }
    }
  }, [selectedEvent, visibleIndex.events]);

  // Send "alert viewed" events to backend
  useEffect(() => {
    if (!selectedEvent || !alertId) return;
    if (selectedEvent.firstViewedAt) return;
    console.log(selectedEvent.id, selectedEvent.firstViewedAt);

    console.debug('Marking event as viewed', selectedEvent.id);
    tracking.track(
      { type: 'alert.event.viewed', eventId: selectedEvent.id },
      {
        immediate: true,
      }
    );
  }, [alertId, selectedEvent?.firstViewedAt, selectedEvent?.id]);

  return (
    <Provider
      first={first}
      last={last}
      prev={prev}
      next={next}
      nextUnviewed={nextUnviewed}
      selectedEvent={selectedEvent}
      setSelectedEventId={navigateToEvent}
      events={events}
      selectedEventId={selectedEventId}
      nextEventId={nextEventId}
      prevEventId={prevEventId}
      nextUnviewedEventId={nextUnviewedEventId}
      unviewedCount={unviewedCount}
      totalEvents={totalEvents}
      mode={mode}
      setMode={setMode}
    >
      {props.children}
    </Provider>
  );
};

export const useAlertEventsCtx = () => useContext('useAlertEventsCtx');
